import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import api from '@/libs/axiosInstance';
import type { ApiResponse, BrandSettings } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export interface UpdateOutreachEmailDto {
  email: string;
}

type SuccessMessageKey = 'successUpdated' | 'successRemoved';

function useOutreachEmailMutation<TVariables>(
  brandId: string,
  mutationFn: (data: TVariables) => Promise<ApiResponse<BrandSettings>>,
  successMessageKey: SuccessMessageKey
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { refreshCurrentUser } = useAuth();

  return useMutation<ApiResponse<BrandSettings>, Error, TVariables>({
    mutationFn,
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['brandSettings', brandId] });
      queryClient.invalidateQueries({ queryKey: ['currentBrand'] });
      await refreshCurrentUser();

      toaster.success({
        title: t('settings.outreachEmail.successTitle'),
        description: t(`settings.outreachEmail.${successMessageKey}`),
      });
    },
    onError: () => {
      toaster.error({
        title: t('common.error'),
        description: t('settings.outreachEmail.errorDescription'),
      });
    },
  });
}

export function useUpdateOutreachEmailAction(brandId: string) {
  return useOutreachEmailMutation<UpdateOutreachEmailDto>(
    brandId,
    async (data) => {
      const response = await api.patch(
        `/dashboard/brands/${brandId}/settings/outreach-email`,
        data
      );
      return response.data;
    },
    'successUpdated'
  );
}

export function useRemoveOutreachEmailAction(brandId: string) {
  return useOutreachEmailMutation<void>(
    brandId,
    async () => {
      const response = await api.delete(`/dashboard/brands/${brandId}/settings/outreach-email`);
      return response.data;
    },
    'successRemoved'
  );
}
